import { ActionTree, GetterTree, Module, MutationTree } from "vuex"
import { RootState } from ".."
import {
    CalculationsConsumerInfo,
    CalculationsData,
    DateRange,
    NewMonthData,
} from "../../types/moj-elektro"
import axios from "axios"
import moment from "moment"
import {
    getDateKey,
    adjustGroupDataToSixRows,
} from "@/components/external/moj-elektro/utils/table-helpers"
import VueI18n from "vue-i18n"

export interface MojElektroCalculationsState {
    activeMonth: string
    monthsHeight: number
    months: string[]
    data: CalculationsData
    consumerInfo: CalculationsConsumerInfo
    showPlaceholder: boolean
    customPowerLimits: {
        block1: number
        block2: number
        block3: number
        block4: number
        block5: number
    }
    isCustomEnabled: boolean
    loading: boolean
}

const state: MojElektroCalculationsState = {
    activeMonth: "January",
    monthsHeight: 0,
    months: [],
    data: {
        newSystem: {},
        oldSystem: {},
        optimizedSystem: {},
        customSystem: {},
    },
    showPlaceholder: true,
    consumerInfo: {
        billingPower: 0,
        connectionPower: 0,
        consumerType: null,
        consumptionType: "",
        currentLimit: 0,
        isThreePhase: false,
        powerLimits: [
            {
                block1: 0,
                block2: 0,
                block3: 0,
                block4: 0,
                block5: 0,
                connectionPower: 0,
                createdAt: "",
                dateFrom: new Date(),
                dateTo: new Date(),
                isValid: false,
                limitSetBy: "",
                minimumPower: 0,
            },
        ],
        productionPower: null,
        supports15MinuteReadings: false,
        tariffsCount: 2,
        voltageClassification: null,
        yearlyWorkHours: null,
    },
    customPowerLimits: {
        block1: 0,
        block2: 0,
        block3: 0,
        block4: 0,
        block5: 0,
    },
    isCustomEnabled: false,
    loading: false,
}

const mutations: MutationTree<MojElektroCalculationsState> = {
    setActiveMonth(state, month: string) {
        state.activeMonth = month
    },
    setMonthsHeight(state, height: number) {
        state.monthsHeight = height
    },
    setMonths(state, months: string[]) {
        state.months = months
    },
    setData(state, data: CalculationsData) {
        state.data = data
    },
    setShowPlaceholder(state, showPlaceholder: boolean) {
        state.showPlaceholder = showPlaceholder
    },
    setConsumerInfo(state, consumerInfo: CalculationsConsumerInfo) {
        state.consumerInfo = consumerInfo
    },
    setCustomPowerLimits(
        state,
        customPowerLimits: MojElektroCalculationsState["customPowerLimits"],
    ) {
        state.customPowerLimits = customPowerLimits
    },
    setIsCustomEnabled(state, isCustomEnabled: boolean) {
        state.isCustomEnabled = isCustomEnabled
    },
    setLoading(state, loading: boolean) {
        state.loading = loading
    },
}

const actions: ActionTree<MojElektroCalculationsState, RootState> = {
    setActiveMonth({ commit }, month: string) {
        commit("setActiveMonth", month)
    },
    setMonthsHeight({ commit }, height: number) {
        commit("setMonthsHeight", height)
    },
    setShowPlaceholder({ commit }, value: boolean) {
        commit("setShowPlaceholder", value)
    },
    updateMonthsArray({ commit }, dateRange: DateRange) {
        const months: string[] = []
        const startDate = new Date(dateRange.startDate)
        const endDate = new Date(dateRange.endDate)

        const monthNames = [
            "jan",
            "feb",
            "mar",
            "apr",
            "may",
            "jun",
            "jul",
            "aug",
            "sep",
            "oct",
            "nov",
            "dec",
        ]

        const startYear = startDate.getFullYear()
        const startMonth = startDate.getMonth()
        const endYear = endDate.getFullYear()
        const endMonth = endDate.getMonth()

        const totalMonths = (endYear - startYear) * 12 + (endMonth - startMonth)

        for (let i = 0; i <= totalMonths; i++) {
            const date = new Date(startYear, startMonth + i)
            const month = monthNames[date.getMonth()]
            const year = date.getFullYear()
            months.push(`${month} ${year}`)
        }

        commit("setMonths", months)
        commit("setActiveMonth", months[0]) // Set activeMonth to the first month in the new array
    },
    async fetchData({ commit, rootGetters, dispatch }, errorMessageText) {
        commit("setLoading", true)

        try {
            const vt: number | null = rootGetters["mojElektroFiltersModule/getVt"]
            const mt: number | null = rootGetters["mojElektroFiltersModule/getMt"]
            const et: number | null = rootGetters["mojElektroFiltersModule/getEt"]
            const dateRange: DateRange | null = rootGetters["mojElektroFiltersModule/dateRange"]
            const selectedDeviceId: number | null =
                rootGetters["mojElektroFiltersModule/getSelectedDeviceId"]

            const customPowerLimits = state.isCustomEnabled ? state.customPowerLimits : null

            const payload = {
                data: {
                    vt: vt ?? 0,
                    mt: mt ?? 0,
                    et: et ?? 0,
                    from: dateRange?.startDate
                        ? moment(dateRange.startDate).format("YYYY-MM-DD")
                        : null,
                    to: dateRange?.endDate ? moment(dateRange.endDate).format("YYYY-MM-DD") : null,
                    customPowerLimits: customPowerLimits,
                },
            }

            const url = `/pricing/abstract-devices/${selectedDeviceId}/calculations`
            const res = await axios.post(url, payload)

            commit("setData", res.data.data.calculations)
            commit("setConsumerInfo", res.data.data.consumerInfo)
        } catch (error) {
            dispatch(
                "toastMessageModule/showMessage",
                { message: errorMessageText, color: "red" },
                { root: true },
            )

            throw error
        } finally {
            commit("setLoading", false)
        }
    },
    getCombinedTableData({ state }, payload: { tableName: string; i18n: VueI18n; month: string }) {
        const { tableName, i18n, month } = payload
        const dateKey = getDateKey(month)
        let calculationData: Record<string, NewMonthData> = {}

        if (tableName === "optimized") {
            calculationData = state.data.optimizedSystem
        }

        if (tableName === "new") {
            calculationData = state.data.newSystem
        }

        if (tableName === "custom") {
            calculationData = state.data.customSystem
        }

        const energyDataFromState = calculationData[dateKey]?.energy || []
        const networkFeeEnergyDataFromState = calculationData[dateKey]?.networkEnergy || []
        const networkFeePowerDataFromState = calculationData[dateKey]?.networkPower || []
        const totalCost = calculationData[dateKey]?.totalCost || {
            amount: "",
            unit: "",
        }
        const excessivePowerBlocksDataFromState =
            calculationData[dateKey]?.excessivePowerBlocks || []

        const energyData = energyDataFromState.map((item) => ({ ...item }))
        const networkFeeEnergyData = networkFeeEnergyDataFromState.map((item) => ({ ...item }))
        const excessivePowerBlocksData = excessivePowerBlocksDataFromState.map((item) => ({
            occurrences: item.occurrences,
            excessivePower: item.value,
            excessivePowerCost: item.totalCost,
        }))
        const networkFeePowerData = networkFeePowerDataFromState.map((item, index) => ({
            ...item,
            ...excessivePowerBlocksData[index],
        }))

        if (energyData.length > 0) {
            energyData[energyData.length - 1].isTotal = true
        }

        if (networkFeeEnergyData.length > 0) {
            networkFeeEnergyData[networkFeeEnergyData.length - 1].isTotal = true
        }

        if (networkFeePowerData.length > 0) {
            networkFeePowerData[networkFeePowerData.length - 1].isTotal = true
        }

        const adjustedNetworkFeePowerData = adjustGroupDataToSixRows(networkFeePowerData)
        const adjustedNetworkFeeEnergyData = adjustGroupDataToSixRows(networkFeeEnergyData)

        const grandTotal = {
            mode: "span",
            label: "",
            html: false,
            children: [
                {
                    totalCost: {
                        amount: totalCost.amount,
                        unit: totalCost.unit,
                    },
                    isGrandTotal: true,
                    category: "total",
                },
            ],
        }

        const energyGroup = {
            mode: "span",
            label: i18n.t("moj_elektro.calculations.energy"),
            html: false,
            children: energyData,
        }

        const networkFeeEnergyGroup = {
            mode: "span",
            label: i18n.t("moj_elektro.calculations.network_fee_energy"),
            html: false,
            children: adjustedNetworkFeeEnergyData,
        }

        const networkFeePowerGroup = {
            mode: "span",
            label: i18n.t("moj_elektro.calculations.network_fee_power"),
            html: false,
            children: adjustedNetworkFeePowerData,
        }

        return [grandTotal, energyGroup, networkFeePowerGroup, networkFeeEnergyGroup]
    },
}

const getters: GetterTree<MojElektroCalculationsState, RootState> = {
    activeMonth: (state) => state.activeMonth,
    monthsHeight: (state) => state.monthsHeight,
    months: (state) => state.months,
    data: (state) => state.data,
    formattedActiveMonth: (state) => {
        const [monthName, year] = state.activeMonth.split(" ")
        const monthNameLower = monthName.toLowerCase()

        return `${monthNameLower}-${year}`
    },
    showPlaceholder: (state) => state.showPlaceholder,
    consumerInfo: (state) => state.consumerInfo,
    customPowerLimits: (state) => state.customPowerLimits,
    isCustomEnabled: (state) => state.isCustomEnabled,
    loading: (state) => state.loading,
}

const mojElektroCalculationsModule: Module<MojElektroCalculationsState, RootState> = {
    namespaced: true,
    state,
    mutations,
    actions,
    getters,
}

export default mojElektroCalculationsModule
